import React, { useEffect, useRef, useState } from "react";
import "../mobile.css";
import {
  installZxing, loadZxing, pickCloseFocusCamera, fullFrameRegion, decodePhoto,
} from "./zxingDetector";

// In-app QR scanner (plan section 4). Uses the `qr-scanner` library (loaded only when a scanner
// opens) for the camera. The decoder: the browser's own BarcodeDetector where there is one
// (Android), else ZXing (./zxingDetector.js: iPhone Safari has none, and qr-scanner's own jsQR
// worker missed small or soft codes such as a beacon's e-paper screen), else that worker if ZXing
// can't load. With a detector it reads the whole frame, not the middle 400 x 400.
// On an iPhone it switches to the multi-lens back camera, which focuses up close like the Camera
// app (pickCloseFocusCamera); a camera button cycles the back cameras. "Take a photo" opens the
// phone's own camera and reads the picture. Needs HTTPS (or localhost).
// Calls onScan(text) once per distinct code; the caller decides where it goes (mobile/scanTarget.js).

export function cameraErrorMessage(err) {
  const name = (err && (err.name || (typeof err === "string" ? err : ""))) || "";
  const msg = (err && err.message) || String(err || "");
  if (/insecure|secure context/i.test(msg)) {
    return "The camera only works on the secure (https) JobQ site.";
  }
  if (/NotAllowed|Permission|denied/i.test(name + msg)) {
    return "JobQ isn't allowed to use the camera. Allow camera access for this site (on iPhone: Settings › Safari › Camera, or tap aA › Website Settings), then try again. You can also type the code below.";
  }
  if (/NotFound|no camera|Camera not found|OverconstrainedError/i.test(name + msg)) {
    return "No camera was found on this device. Type the code below instead.";
  }
  if (/NotReadable|TrackStart|in use/i.test(name + msg)) {
    return "The camera is busy in another app. Close it and try again.";
  }
  return `The camera could not start (${msg || name || "unknown error"}). Type the code below instead.`;
}

const CAMERA_KEY = "jq.scanner.camera"; // the back camera this phone last switched to by hand

function readCameraChoice() {
  try { return window.localStorage.getItem(CAMERA_KEY) || null; } catch (e) { return null; }
}
function saveCameraChoice(id) {
  try { window.localStorage.setItem(CAMERA_KEY, id); } catch (e) { /* private mode: not remembered */ }
}

// Keep focusing while the phone moves (a no-op where the browser can't).
function focusContinuously(video) {
  try {
    const track = video && video.srcObject && video.srcObject.getVideoTracks
      ? video.srcObject.getVideoTracks()[0] : null;
    const caps = track && track.getCapabilities ? track.getCapabilities() : null;
    if (caps && Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
      track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
    }
  } catch (e) { /* not supported */ }
}

export default function QrScanner({ onScan, paused = false, testId = "qr-scanner" }) {
  const videoRef = useRef(null);
  const photoRef = useRef(null);
  const scannerRef = useRef(null);
  const libRef = useRef(null);
  const onScanRef = useRef(onScan);
  const lastRef = useRef({ text: null, at: 0 });
  const [status, setStatus] = useState("starting"); // starting | scanning | error
  const [error, setError] = useState(null);
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [cameras, setCameras] = useState([]); // back cameras by label, when there are several
  const [cameraLabel, setCameraLabel] = useState(null);
  const [photoNote, setPhotoNote] = useState(null);
  const [readingPhoto, setReadingPhoto] = useState(false);

  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  const report = (text) => {
    if (!text) return;
    const now = Date.now();
    // The same code stays in view for a while: report it once (again after 3 s).
    if (lastRef.current.text === text && now - lastRef.current.at < 3000) return;
    lastRef.current = { text, at: now };
    if (onScanRef.current) onScanRef.current(text);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.isSecureContext || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("insecure context: no camera access");
      }
      const { default: QrScannerLib } = await import("qr-scanner");
      libRef.current = QrScannerLib;
      try {
        await installZxing();
      } catch (e) {
        console.warn("QR scanner: ZXing decoder unavailable, using the built-in one:", e && e.message);
      }
      if (cancelled || !videoRef.current) return;
      const fullFrame = "BarcodeDetector" in window;
      const scanner = new QrScannerLib(
        videoRef.current,
        (result) => report(result && typeof result === "object" ? result.data : result),
        {
          preferredCamera: "environment",
          highlightScanRegion: !fullFrame,
          highlightCodeOutline: true,
          returnDetailedScanResult: true,
          maxScansPerSecond: fullFrame ? 6 : 8,
          ...(fullFrame ? {
            calculateScanRegion: (v) => fullFrameRegion(v.videoWidth, v.videoHeight),
          } : {}),
        }
      );
      scannerRef.current = scanner;
      await scanner.start();
      if (cancelled) return;
      setStatus("scanning");
      scanner.hasFlash().then((f) => { if (!cancelled) setHasFlash(Boolean(f)); }).catch(() => {});

      // The close-focus camera (iPhone). Labels are only listed once the camera is allowed.
      try {
        const list = await QrScannerLib.listCameras(true);
        if (cancelled) return;
        const back = (list || []).filter((c) => /back|rear|environment/i.test(c.label || ""));
        const chosen = readCameraChoice();
        const pick = (chosen && back.find((c) => c.id === chosen)) || pickCloseFocusCamera(back);
        if (pick) {
          await scanner.setCamera(pick.id);
          if (cancelled) return;
          setCameraLabel(pick.label);
        }
        if (back.length > 1) setCameras(back);
        focusContinuously(videoRef.current);
      } catch (e) { /* keep the default camera */ }
    })().catch((e) => {
      if (cancelled) return;
      setStatus("error");
      setError(cameraErrorMessage(e));
    });
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        try { s.stop(); s.destroy(); } catch (e) { /* already gone */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const s = scannerRef.current;
    if (!s || status !== "scanning") return;
    if (paused) s.pause();
    else s.start().catch(() => {});
  }, [paused, status]);

  const toggleFlash = async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      await s.toggleFlash();
      setFlashOn(s.isFlashOn());
    } catch (e) { /* not supported after all */ }
  };

  const nextCamera = async () => {
    const s = scannerRef.current;
    if (!s || cameras.length < 2) return;
    const i = cameras.findIndex((c) => c.label === cameraLabel);
    const next = cameras[(i + 1) % cameras.length];
    try {
      await s.setCamera(next.id);
      setCameraLabel(next.label);
      setFlashOn(false);
      saveCameraChoice(next.id);
      focusContinuously(videoRef.current);
    } catch (e) { /* that camera can't start: stay */ }
  };

  const takePhoto = () => {
    setPhotoNote(null);
    if (photoRef.current) photoRef.current.click();
  };

  const onPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // the same photo can be picked again
    if (!file) return;
    setReadingPhoto(true);
    let text = null;
    try {
      let Detector = null;
      try { Detector = await loadZxing(); } catch (err) { Detector = null; }
      const Lib = libRef.current;
      text = await decodePhoto(file, {
        Detector,
        fallback: Lib ? async (f) => {
          const r = await Lib.scanImage(f, { returnDetailedScanResult: true });
          return r && r.data;
        } : null,
      });
    } catch (err) {
      text = null;
    }
    setReadingPhoto(false);
    // iPhone stops the live camera while its own camera is open: start it again.
    const s = scannerRef.current;
    if (s && !paused) s.start().catch(() => {});
    if (text) {
      lastRef.current = { text: null, at: 0 };
      report(text);
    } else {
      setPhotoNote("No QR code found in that photo. Fill the frame with the code and keep it sharp, or type the code.");
    }
  };

  return (
    <div className="jq-scanner" data-testid={testId}>
      <div className="jq-scanner-view">
        {/* playsInline + muted: iPhone only plays a camera stream inline when both are set. */}
        <video ref={videoRef} className="jq-scanner-video" playsInline muted aria-label="Camera view" />
        {status === "starting" && (
          <div className="jq-scanner-overlay" role="status">
            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" /> Starting the camera…
          </div>
        )}
        {status === "error" && (
          <div className="jq-scanner-overlay jq-scanner-error" role="alert">{error}</div>
        )}
        {status === "scanning" && hasFlash && (
          <button type="button" className="jq-icon-btn jq-scanner-flash" onClick={toggleFlash} aria-pressed={flashOn} aria-label={flashOn ? "Light off" : "Light on"}>
            <i className={`fas ${flashOn ? "fa-lightbulb" : "fa-bolt"}`} aria-hidden="true" />
          </button>
        )}
        {status === "scanning" && cameras.length > 1 && (
          <button type="button" className="jq-icon-btn jq-scanner-lens" onClick={nextCamera} aria-label={`Switch camera (now ${cameraLabel || "default"})`} title={cameraLabel || "Switch camera"}>
            <i className="fas fa-camera-rotate" aria-hidden="true" />
          </button>
        )}
      </div>
      {status === "scanning" && <div className="jq-scanner-hint">Point the camera at the QR code on the traveler or label.</div>}
      <input ref={photoRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} data-testid={`${testId}-photo-input`} />
      <button type="button" className="btn btn-outline-secondary w-100 mt-2" onClick={takePhoto} disabled={readingPhoto}>
        {readingPhoto ? "Reading the photo…" : <><i className="fas fa-camera me-2" aria-hidden="true" />Won't scan? Take a photo instead</>}
      </button>
      {photoNote && <div className="alert alert-warning mt-2 mb-0" role="alert">{photoNote}</div>}
    </div>
  );
}
